import { Component, h, Listen, Prop, State } from '@stencil/core';

import { animationFrameScheduler, fromEvent, interval, Subscription } from 'rxjs';

import {
  debounceTime,
  filter,
  map,
  subscribeOn,
  switchMap,
  take,
  takeUntil,
  tap,
} from 'rxjs/operators';

import { FloatingButtonState } from '@entities/FloatingButtonState';
import { SentimentsPosition } from '@entities/Position';
import { ServiceType } from '@entities/ServiceType';
import { WidgetPosition } from '@entities/WidgetPosition';
import SnowplowService from '@services/SnowplowService';
import * as Utils from '@services/UtilsService';
import { WidgetLocation } from '@services/WidgetLocation';
import appStore from '@stores/app.store';

interface ExtendedTouchEvent extends TouchEvent {
  offsetX: number;
  offsetY: number;
}

@Component({
  tag: 'wog-floating-action-button',
})
export class FloatingActionButton {
  @Prop() isMobile = false;
  @Prop() position: WidgetPosition = WidgetPosition.BottomLeft;
  @Prop() widgetLabel: string;
  @State() floatingButtonState: FloatingButtonState = FloatingButtonState.Initial;
  @State() hasPositionChanged = false;

  private rollOutSubscription: Subscription;
  private buttonContainer: HTMLDivElement;
  private dragSubscription: Subscription;
  private mouseUpSubscription: Subscription;
  private iosTouchForceSubscription: Subscription;
  private orientationChangeSubscription: Subscription;

  private widgetLocation = new WidgetLocation();

  private readonly gapSpace = 65;

  componentWillLoad() {
    this.hasPositionChanged = this.widgetLocation.hasWidgetMoved;
  }

  componentDidLoad() {
    if (this.isMobile) {
      this.positionReset();
      this.applyDraggableButton();
    } else {
      if (!this.widgetLocation.hasWidgetMoved) {
        this.widgetLocation.hasWidgetMoved = false;
        this.hasPositionChanged = false;
      }
      this.handleRollout();
      Utils.unsubscribeAll(
        this.dragSubscription,
        this.mouseUpSubscription,
        this.iosTouchForceSubscription,
        this.orientationChangeSubscription,
      );
    }
  }

  disconnectedCallback() {
    Utils.unsubscribeAll(
      this.rollOutSubscription,
      this.dragSubscription,
      this.mouseUpSubscription,
      this.iosTouchForceSubscription,
      this.orientationChangeSubscription,
    );
  }

  @Listen('resize', { target: 'window' })
  handleScroll() {
    if (this.isMobile) {
      this.positionReset();
    }
  }

  @Listen('orientationchange', { target: 'window' })
  handleOrientation() {
    this.widgetLocation.left = null;
    this.widgetLocation.top = null;
    this.buttonContainer.removeAttribute('style');
    this.widgetLocation.hasWidgetMoved = null;
    this.hasPositionChanged = false;
  }

  private easeInSine(t, b, _c, d) {
    const c = _c - b;
    return -c * Math.cos((t / d) * (Math.PI / 2)) + c + b;
  }

  private handleRollout() {
    const status = this.widgetLocation.rollOutStatus;
    if (appStore.get('serviceType') === ServiceType.InformationalService && !status) {
      const cleanUp$ = interval(3000).pipe(
        tap(() => {
          this.floatingButtonState = FloatingButtonState.Leave;
        }),
        take(1),
      );

      this.rollOutSubscription = interval(60000)
        .pipe(
          take(1),
          tap(() => {
            this.floatingButtonState = FloatingButtonState.Enter;
            this.widgetLocation.rollOutStatus = true;
          }),
          switchMap(() => cleanUp$),
        )
        .subscribe();
    }
  }

  /**
   * Handle floating button enter
   */
  private handleFloatingButtonEnter = (event: Event) => {
    event.preventDefault();
    Utils.unsubscribeAll(this.rollOutSubscription);
    if (appStore.get('floatingButtonStateTimeout')) {
      this.floatingButtonState = FloatingButtonState.Enter;
    }
  };

  /**
   * Handle floating button leave
   */
  private handleFloatingButtonLeave = (event: Event) => {
    event.preventDefault();
    if (appStore.get('floatingButtonStateTimeout')) {
      this.floatingButtonState = FloatingButtonState.Leave;
    }
  };

  /**
   * Handle the fab icon withing the viewport
   * @param value
   * @param maxValue
   */
  private handleSentimentsWithinViewPort(value: number, maxValue: number) {
    return value < 0 ? 0 : value > maxValue - this.gapSpace ? maxValue - this.gapSpace : value;
  }

  /**
   * Get client dimensions
   */
  private getClientSize(isMobile: boolean): { clientWidth: number; clientHeight: number } {
    if (isMobile) {
      const { clientWidth } = document.documentElement;
      const height = window.innerHeight * 0.01 * 100;
      return {
        clientWidth: clientWidth,
        clientHeight: height,
      };
    }
    const { clientWidth, clientHeight } = document.documentElement;
    return {
      clientWidth,
      clientHeight,
    };
  }

  /**
   * Reset the icon position
   */
  private positionReset() {
    const leftValue = this.widgetLocation.left;
    const topValue = this.widgetLocation.top;
    const leftGap = 10;

    if (topValue) {
      this.buttonContainer.style.top = `${topValue}px`;
    }

    if (leftValue) {
      const { clientWidth } = this.getClientSize(this.isMobile);
      const middle = clientWidth / 2;
      const isLeftMove = leftValue <= middle;
      const endPosition = isLeftMove ? leftGap : clientWidth - this.gapSpace;
      this.buttonContainer.style.left = `${endPosition}px`;
    }
  }

  /**
   * Subscribe to draggable fab button
   */
  private applyDraggableButton() {
    const options = { passive: false };

    // Touch Move
    const mousemove$ = fromEvent<TouchEvent>(document, 'touchmove', options).pipe(
      map(e => {
        e.preventDefault();
        return e.touches[0];
      }),
    );

    // Touch End
    const mouseup$ = fromEvent<TouchEvent>(this.buttonContainer, 'touchend');

    // Touch Move
    const mousedown$ = fromEvent<ExtendedTouchEvent>(
      this.buttonContainer,
      'touchstart',
      options,
    ).pipe(
      map(e => {
        const rect = (e.target as HTMLDivElement).getBoundingClientRect();
        e.offsetX = e.touches[0].clientX - rect.left;
        e.offsetY = e.touches[0].clientY - rect.top;
        return e;
      }),
    );

    // This is a fix for iOS 12.X or older versions
    // https://stackoverflow.com/questions/49500339/cant-prevent-touchmove-from-scrolling-window-on-ios
    this.iosTouchForceSubscription = fromEvent(document, 'touchforcechange', options).subscribe();

    this.mouseUpSubscription = mouseup$
      .pipe(
        debounceTime(100),
        filter(() => this.widgetLocation.hasWidgetMoved),
        map((event: TouchEvent) => ({
          middle: this.getClientSize(this.isMobile).clientWidth / 2,
          position: {
            x: event.changedTouches[0].clientX,
            y: event.changedTouches[0].clientY,
          },
        })),
        switchMap(({ middle, position }: { middle: number; position: SentimentsPosition }) => {
          const isLeftMove = position.x <= middle;
          const endPosition = isLeftMove
            ? 10
            : this.getClientSize(this.isMobile).clientWidth - this.gapSpace;
          return Utils.createTween(this.easeInSine, position.x, endPosition, 300);
        }),
      )
      .subscribe((value: number) => {
        this.buttonContainer.style.left = `${value}px`;
        this.widgetLocation.left = value;
      });

    const drag$ = mousedown$.pipe(
      debounceTime(100),
      switchMap((startEvent: ExtendedTouchEvent) => {
        return mousemove$.pipe(
          map((moveEvent: Touch) => {
            const { clientWidth, clientHeight } = this.getClientSize(this.isMobile);
            return {
              left: this.handleSentimentsWithinViewPort(
                moveEvent.clientX - startEvent.offsetX,
                clientWidth,
              ),
              top: this.handleSentimentsWithinViewPort(
                moveEvent.clientY - startEvent.offsetY,
                clientHeight,
              ),
            };
          }),
          takeUntil(mouseup$),
        );
      }),
    );

    this.dragSubscription = drag$.pipe(subscribeOn(animationFrameScheduler)).subscribe(position => {
      this.hasPositionChanged = true;
      this.buttonContainer.style.left = `${position.left}px`;
      this.buttonContainer.style.top = `${position.top}px`;
      this.widgetLocation.left = position.left;
      this.widgetLocation.top = position.top;
      const value = this.widgetLocation.hasWidgetMoved;
      if (!value) {
        this.widgetLocation.hasWidgetMoved = true;
        new SnowplowService().customTrackEvent({
          category: 'sentiments-mobileview-widget',
          action: 'icon-movement',
        });
      }
    });
  }

  private renderText() {
    if (this.floatingButtonState === FloatingButtonState.Enter) {
      return (
        <h3 class="wog--text-foundation wog--base-title wog--floating-text-animation wog--block wog--text-white">
          Help us improve
        </h3>
      );
    }
    return;
  }

  private getRootPosition(changed: boolean, position: WidgetPosition) {
    if (changed) {
      return {};
    }
    return {
      'wog--float-bottom-left': position === WidgetPosition.BottomLeft,
      'wog--float-bottom-right': position === WidgetPosition.BottomRight,
    };
  }

  render() {
    let events = {};
    if (!this.isMobile) {
      events = {
        onMouseEnter: this.handleFloatingButtonEnter,
        onMouseLeave: this.handleFloatingButtonLeave,
        onfocus: this.handleFloatingButtonEnter,
        onblur: this.handleFloatingButtonLeave,
      };
    }
    const hasEntered = this.floatingButtonState === FloatingButtonState.Enter;
    const styles = {
      'wog--flex': true,
      'wog--items-center': true,
      'wog--justify-center': true,
      'wog--inline-flex': true,
      'wog--floating-button': true,
      'wog--floating-button_hover': hasEntered,
      ...this.getRootPosition(this.hasPositionChanged, this.position),
    };
    const buttonAriaLabel = `${this.widgetLabel} popup`;
    return (
      <div ref={el => (this.buttonContainer = el as HTMLDivElement)} class={styles} {...events} tabIndex={0} role="button" aria-label={buttonAriaLabel}>
        <wog-icon-smiley-face exit={hasEntered} />
        {this.renderText()}
      </div>
    );
  }
}
